import { FormHelper } from '@presentation/components/FormHelper';
import { parseFlexibleDateToIso } from '@shared/utils/fmtDate';
import { ToastService } from '@presentation/components/Toast';
import { AppContext } from '@application/context/AppContext';
import { ComptabiliserFactureUseCase } from '@application/usecases/ComptabiliserFactureUseCase';
import { FactureInputSchema } from '@shared/validation/FactureSchema';
import { numeroFactureDejaPris } from '@domain/paie/SalarieUniqueness';
import { loadRegister } from '@presentation/shell/localCrud';
import { compteOhada } from '@shared/validation/identifiantsGabon';
import { loadBaremeTva } from '@application/comptabilite/BaremeTvaStore';
import type { ConditionsReglement } from '@shared/types/Tiers';
import { z } from 'zod';
import {
  compteProduitOptions,
  DEVISE_OPTIONS,
  factureLineRow,
  lierBaremeAuxFactures,
  factureTotalsBar,
  wireLiveTotals,
} from './factureFormShared';

/** Les champs date affichent JJ/MM/AAAA ; les schémas attendent AAAA-MM-JJ. */
function dateIso(v: FormDataEntryValue | null): string {
  const raw = typeof v === 'string' ? v.trim() : '';
  return parseFlexibleDateToIso(raw) ?? raw;
}

interface TiersLocal {
  id: string;
  code: string;
  type: 'client' | 'fournisseur' | 'mixte';
  raison_sociale: string;
  conditions_reglement?: ConditionsReglement;
}

const HeaderSchema = z.object({
  numero: z.string().min(1, 'Numéro requis').max(50),
  date: z.string().min(1),
  date_echeance: z.string().min(1),
  client_id: z.string().min(1, 'Client requis'),
  client_compte: compteOhada,
  objet: z.string().optional(),
  devise: z.enum(['XAF', 'EUR', 'USD']).optional(),
  axe_cdc: z.string().optional(),
});

function echeanceFromConditions(base: string, cond?: ConditionsReglement): string {
  const days = cond === 'comptant' ? 0 : cond === '60j' ? 60 : cond === '90j' ? 90 : 30;
  const d = new Date(base);
  if (Number.isNaN(d.getTime())) return base;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export class FactureClientFormView {
  static render(container: HTMLElement, onSaved: () => void, onCancel = onSaved): void {
    void FactureClientFormView.renderAsync(container, onSaved, onCancel);
  }

  private static async renderAsync(
    container: HTMLElement,
    onSaved: () => void,
    onCancel: () => void,
  ): Promise<void> {
    const ctx = AppContext.getInstance();
    const tid = ctx.tenantContext.tenantId();
    const allTiers = await loadRegister<TiersLocal>(tid, 'tiers');
    const bareme = await loadBaremeTva(tid);
    const tiers = allTiers.filter((t) => t.type === 'client' || t.type === 'mixte');
    const today = new Date().toISOString().slice(0, 10);
    const echeance = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    const tiersOptions =
      tiers.length > 0
        ? tiers.map((t) => ({ value: t.code || t.id, label: `${t.code} — ${t.raison_sociale}` }))
        : [{ value: '', label: 'Aucun client — créez-en dans Tiers' }];
    const compteOpts = compteProduitOptions();

    container.innerHTML = `
      <h2 style="margin-top:0;">Nouvelle facture client</h2>
      <form id="fac-form" style="max-width:960px;background:white;padding:24px;border:1px solid #e1e4e8;border-radius:8px;">
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;">
          ${FormHelper.field('numero', 'N° facture', 'text', { required: true, placeholder: 'FV-2026-001' })}
          ${FormHelper.field('date', 'Date', 'date', { required: true, value: today })}
          ${FormHelper.field('date_echeance', 'Échéance', 'date', { required: true, value: echeance })}
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          ${FormHelper.select('client_id', 'Client', tiersOptions, { required: true })}
          ${FormHelper.field('client_compte', 'Compte client (411xxx)', 'text', { required: true, placeholder: '411001', value: '411001' })}
        </div>
        ${FormHelper.field('objet', 'Objet / libellé', 'text', { placeholder: 'Ex. Prestation conseil T1 2026' })}
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          ${FormHelper.select('devise', 'Devise', DEVISE_OPTIONS, { value: 'XAF' })}
          ${FormHelper.field('axe_cdc', 'Axe CDC (en-tête)', 'text', { placeholder: 'Centre de coût global' })}
        </div>
        <hr style="border:0;border-top:1px solid #e1e4e8;margin:16px 0;" />
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <h3 style="margin:0;font-size:1rem;">Lignes de facture</h3>
          <button type="button" id="add-line" class="btn btn-secondary" style="padding:6px 12px;">+ Ajouter une ligne</button>
        </div>
        <div id="fac-lignes"></div>
        <p id="fac-tva-status" role="status" style="font-size:0.8rem;color:#586069;margin:8px 0 0;"></p>
        ${factureTotalsBar({ ht: 'fac-total-ht', tva: 'fac-total-tva', ttc: 'fac-total-ttc' })}
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
          <button type="button" id="cancel" style="padding:8px 16px;background:white;color:#586069;border:1px solid #e1e4e8;border-radius:4px;cursor:pointer;">Annuler</button>
          <button type="submit" style="padding:8px 16px;background:#0a4d7e;color:white;border:none;border-radius:4px;font-weight:600;cursor:pointer;">Comptabiliser</button>
        </div>
      </form>
    `;

    const form = container.querySelector<HTMLFormElement>('#fac-form')!;
    const lignesBox = form.querySelector<HTMLElement>('#fac-lignes')!;
    let lineCount = 0;
    const liaison = lierBaremeAuxFactures({
      form,
      lignesBox,
      lineClass: 'fac-line',
      dateFieldName: 'date',
      bareme,
      statusEl: form.querySelector<HTMLElement>('#fac-tva-status'),
      aujourdhui: today,
    });

    const addLine = (): void => {
      const idx = lineCount++;
      const row = document.createElement('div');
      const tva = liaison.courant();
      row.innerHTML = factureLineRow(idx, 'fac-line', compteOpts, '7060', tva.options, tva.defaut);
      lignesBox.appendChild(row.firstElementChild!);
    };

    addLine();
    wireLiveTotals(form, lignesBox, 'fac-line', {
      ht: 'fac-total-ht',
      tva: 'fac-total-tva',
      ttc: 'fac-total-ttc',
    });

    form.querySelector('#add-line')?.addEventListener('click', () => addLine());
    lignesBox.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-remove]');
      if (!btn) return;
      if (lignesBox.querySelectorAll('.fac-line').length <= 1) {
        ToastService.warning('Au moins une ligne est requise');
        return;
      }
      btn.closest('.fac-line')?.remove();
      form.dispatchEvent(new Event('input', { bubbles: true }));
    });

    const clientSelect = form.querySelector<HTMLSelectElement>('[name="client_id"]');
    const dateInput = form.querySelector<HTMLInputElement>('[name="date"]');
    const echeanceInput = form.querySelector<HTMLInputElement>('[name="date_echeance"]');
    const syncEcheance = (): void => {
      const code = clientSelect?.value;
      const tier = tiers.find((t) => (t.code || t.id) === code);
      const base = dateInput?.value || today;
      if (echeanceInput && tier?.conditions_reglement) {
        echeanceInput.value = echeanceFromConditions(base, tier.conditions_reglement);
      }
    };
    clientSelect?.addEventListener('change', syncEcheance);
    dateInput?.addEventListener('change', syncEcheance);

    container.querySelector('#cancel')?.addEventListener('click', () => onCancel());

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      FormHelper.clearFieldErrors(form);
      const fd = new FormData(form);
      const header = HeaderSchema.safeParse({
        numero: fd.get('numero'),
        date: dateIso(fd.get('date')),
        date_echeance: dateIso(fd.get('date_echeance')),
        client_id: fd.get('client_id'),
        client_compte: fd.get('client_compte'),
        objet: fd.get('objet') || undefined,
        devise: fd.get('devise') || 'XAF',
        axe_cdc: fd.get('axe_cdc') || undefined,
      });
      if (!header.success) {
        ToastService.error(header.error.issues[0]?.message ?? 'Saisie invalide');
        return;
      }
      const headerAxe = header.data.axe_cdc?.trim() || undefined;
      const lignes = Array.from(lignesBox.querySelectorAll<HTMLElement>('.fac-line')).map((row) => {
        const idx = row.dataset.idx ?? '0';
        const lineAxe = String(fd.get(`axe_${idx}`) ?? '').trim() || headerAxe;
        return {
          description: String(fd.get(`desc_${idx}`) ?? ''),
          quantite: Number(fd.get(`qty_${idx}`) ?? 0),
          prix_unitaire: Number(fd.get(`pu_${idx}`) ?? 0),
          taux_tva: Number(fd.get(`tva_${idx}`) ?? 0),
          compte_produit: String(fd.get(`cpt_${idx}`) ?? ''),
          ...(lineAxe ? { axe_cdc: lineAxe } : {}),
        };
      });
      const parsed = FactureInputSchema.safeParse({
        client_id: header.data.client_id,
        date: header.data.date,
        date_echeance: header.data.date_echeance,
        objet: header.data.objet,
        devise: header.data.devise,
        axe_cdc: headerAxe,
        lignes,
      });
      if (!parsed.success) {
        ToastService.error(parsed.error.issues[0]?.message ?? 'Lignes invalides');
        return;
      }
      const existantes = await ctx.factures.list();
      if (numeroFactureDejaPris(existantes, header.data.numero)) {
        FormHelper.markFieldError(form, 'numero', 'Numéro déjà utilisé');
        ToastService.error('Numéro déjà utilisé');
        return;
      }
      const uc = new ComptabiliserFactureUseCase();
      const r = uc.execute(tid, parsed.data, header.data.numero, header.data.client_compte);
      await ctx.factures.add({
        ...r.facture,
        type: 'vente',
        statut: 'comptabilisee',
        objet: header.data.objet,
        devise: header.data.devise ?? 'XAF',
        axe_cdc: headerAxe,
      });
      for (const ecr of r.ecritures) {
        await ctx.ecritures.add({ ...ecr, tenant_id: tid });
      }
      ToastService.success(
        `Facture ${r.facture.numero} comptabilisée — TTC ${r.facture.total_ttc.toLocaleString('fr-FR')} FCFA (${parsed.data.lignes.length} ligne(s))`,
      );
      onSaved();
    });
  }
}
